import { useAuth0 } from "@auth0/auth0-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";

import API_Calls_Template from "@/api/calls/API_Calls_Template";
import AreYouSureModal from "@/components/membership/Modals/AreYouSureModal";
import TemplateNameModal from "@/components/membership/Modals/TemplateNameModal";
import SearchBar from "@/components/SearchBar/SearchBar";
import CatalistOnlyChip from "@/components/sharedComponents/CatalistOnlyChip";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import useGetCachesHandler from "@/hooks/useGetCachesHandler";
import useTemplateNamesHandler from "@/hooks/useTemplateNamesHandler";
import TableStyles from "@/styles/TableStyles";

function TemplatesTable() {
  const navigate = useNavigate();
  const { handleTemplateDataChange } = useUpdateDataContext();
  const { getDisplayName, updateTemplateNamesList } = useTemplateNamesHandler();
  const { getEmptyTemplate } = useGetCachesHandler();
  const {
    deleteTemplate,
    fetchTemplates,
    downloadTemplate,
    getExistingTemplate,
  } = API_Calls_Template();
  const { user } = useAuth0();
  const [templateList, setTemplateList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTemplateList();
    // fetchTemplateList/fetchTemplates are recreated each render;
    // intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplateList = async () => {
    if (!user?.org_name) return;

    const response = await fetchTemplates(user.org_name);
    if (!response.error && response.data) {
      const sortedTemplates = response.data.sort(
        (a, b) => new Date(b.created) - new Date(a.created)
      );
      setTemplateList(sortedTemplates);
    } else {
      console.error(
        "Error fetching template list:",
        response.message || "Unknown error"
      );
    }
    setIsLoading(false);
  };

  const handleTemplateButtonClick = (option) => {
    fetchSelectedTemplate(option);
  };

  const fetchSelectedTemplate = async (option) => {
    const response = await getExistingTemplate(
      option.affiliate.navid,
      option.name,
      option.created
    );
    if (!response.error && response.data) {
      handleTemplateDataChange({
        name: option.name,
        created: option.created,
        template: response.data.template,
        problems: response.data.problems,
        use: response.data.use,
      });
      navigate("/membership/update-data/edit-template");
    } else {
      console.error(
        "Error retrieving template: ",
        response.message || "Unknown error"
      );
    }
  };

  const filteredTemplates =
    searchTerm.length === 0
      ? templateList
      : templateList.filter((template) =>
          template.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

  const handleTemplateDownloadClick = async (item) => {
    const response = await downloadTemplate(
      item.affiliate.navid,
      item.name,
      item.created
    );

    if (!response.error && response.data) {
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } else {
      console.error(
        "Error downloading template:",
        response.message || "Unknown error"
      );
    }
  };

  const handleTemplateDelete = async (item) => {
    const response = await deleteTemplate(
      item.affiliate.navid,
      item.name,
      item.created
    );
    if (!response) {
      console.error(
        "Error deleting template: No response from delete operation."
      );
      return;
    }

    if (!response.error) {
      removeDeletedTemplate(item.created);
      updateTemplateNamesList();
    } else {
      console.error(
        "Error deleting template:",
        response.message || "Unknown error"
      );
    }
  };

  const handleNewTemplateCreation = async (templateName, isCatalistOnly) => {
    const newTemplate = await getEmptyTemplate();

    if (newTemplate) {
      handleTemplateDataChange({
        name: templateName,
        template: newTemplate,
        created: "",
        use: isCatalistOnly ? "catalist-only" : "general",
      });
      navigate("/membership/update-data/edit-template");
    } else {
      console.error(
        "Error handling form submission: Failed to fetch new template."
      );
    }
  };

  const removeDeletedTemplate = (created) => {
    setTemplateList((templateList) =>
      templateList.filter((template) => template["created"] !== created)
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Box sx={{ marginRight: "15px" }}>
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </Box>

        <TemplateNameModal
          title={"Create New Template"}
          handleSubmit={handleNewTemplateCreation}
          isNew={true}
        />
      </Box>
      <TableContainer sx={TableStyles.Container} data-testid="templates-table">
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={TableStyles.THFont} align="left">
                  File Name
                </TableCell>
                <TableCell sx={TableStyles.THFont} align="left">
                  Modified
                </TableCell>
                <TableCell sx={TableStyles.THFont} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.length > 0 ? (
                filteredTemplates.map((row) => (
                  <TableRow
                    key={row.created}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Button
                        className="siteBlue"
                        onClick={() => handleTemplateButtonClick(row)}
                      >
                        Select
                      </Button>
                    </TableCell>
                    <TableCell align="left">
                      {getDisplayName(row.name, row.created)}
                      {row.use === "catalist-only" && <CatalistOnlyChip />}
                    </TableCell>
                    <TableCell align="left">
                      {row.created.replace("T", " ")}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        sx={TableStyles.TableButton}
                        onClick={() => handleTemplateDownloadClick(row)}
                      >
                        <SiteIcons iconstring="download" />
                      </IconButton>
                      <IconButton>
                        <AreYouSureModal
                          id={row}
                          handleAction={handleTemplateDelete}
                          title={`Delete ${row.name}`}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={4}>
                    No templates to display
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}

export default TemplatesTable;
